import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getNonce, getConfig, adminAjax } from './Helpers';
import { 
    getAuth, 
    signInWithPopup, 
    GoogleAuthProvider, 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
    updateProfile
} from 'firebase/auth';
import { 
    getFirestore, 
    collection, 
    addDoc, 
    query, 
    orderBy, 
    limit, 
    onSnapshot,
    serverTimestamp,
    where,
    doc,
    updateDoc,
    setDoc,
    getDocs,
    deleteDoc
} from 'firebase/firestore';
import { 
    getStorage, 
    ref, 
    uploadBytes, 
    getDownloadURL 
} from 'firebase/storage';


// Add this component to handle image loading issues
const SafeImage = ({ src, alt, className, fallbackSrc }) => {
    const [imgSrc, setImgSrc] = useState(src);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        // Check if it's a Google user content URL that needs proxying
        if (src && src.includes('googleusercontent.com')) {
            const proxyUrl = `${adminAjax()}?action=acb_proxy_image&url=${encodeURIComponent(src)}&nonce=${getNonce()}`;
            setImgSrc(proxyUrl);
        } else {
            setImgSrc(src);
        }
        setHasError(false);
    }, [src]);

    const handleError = () => {
        if (!hasError) {
            setHasError(true);
            // Try the original URL first if proxy failed
            if (imgSrc.includes('admin-ajax.php')) {
                setImgSrc(src);
            } else {
                // If original also failed, use fallback
                setImgSrc(fallbackSrc || 'https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg');
            }
        }
    };

    const handleLoad = () => {
        setHasError(false);
    };

    return (
        <img 
            src={imgSrc}
            alt={alt}
            className={className}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={handleError}
            onLoad={handleLoad}
            loading="lazy"
        />
    );
}

const Firebase = () => {
    const [user, setUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [showLogin, setShowLogin] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [signUpDisplayName, setSignUpDisplayName] = useState('');
    const [signUpProfileImage, setSignUpProfileImage] = useState(null);
    const [signUpProfileImagePreview, setSignUpProfileImagePreview] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [firebaseError, setFirebaseError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [usingLocalUsers, setUsingLocalUsers] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState({});
    const [lastMessageTimes, setLastMessageTimes] = useState({});
    const [supportSystemEnabled, setSupportSystemEnabled] = useState(false);
    const [currentUserIsAdmin, setCurrentUserIsAdmin] = useState(false);
    
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const profileImageInputRef = useRef(null);
    const [firebaseConfig, setFirebaseConfig] = useState(null);
    const [app, setApp] = useState(null);
    const [auth, setAuth] = useState(null);
    const [db, setDb] = useState(null);
    const [storage, setStorage] = useState(null);

    useEffect(() => {
        // Get Firebase config from WordPress
        const config = getConfig();
        
        if (config && config.apiKey) {
            setFirebaseConfig(config);
            try {
                const firebaseApp = initializeApp(config);
                const firebaseAuth = getAuth(firebaseApp);
                const firestoreDb = getFirestore(firebaseApp);
                const firebaseStorage = getStorage(firebaseApp);
                
                setApp(firebaseApp);
                setAuth(firebaseAuth);
                setDb(firestoreDb);
                setStorage(firebaseStorage);
                
                // Listen for auth state changes
                const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
                    setUser(user);
                    setLoading(false);
                    if (user) {
                        // //  console.log('User signed in:', user.email);
                        // Update user's online status and save to Firestore
                        updateUserStatus(user, true);
                        // Authenticate with WordPress backend
                        authenticateWithWordPress(user);
                        // //  console.log('User authentication completed');
                    }
                });
                
                // Handle page visibility changes for online/offline status
                const handleVisibilityChange = () => {
                    if (user && db) {
                        if (document.hidden) {
                            updateUserStatus(user, false);
                        } else {
                            updateUserStatus(user, true);
                        }
                    }
                };
                
                document.addEventListener('visibilitychange', handleVisibilityChange);
                
                // Handle beforeunload for logout
                const handleBeforeUnload = () => {
                    if (user && db) {
                        updateUserStatus(user, false);
                    }
                };
                
                window.addEventListener('beforeunload', handleBeforeUnload);
                
                return () => {
                    unsubscribe();
                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                    window.removeEventListener('beforeunload', handleBeforeUnload);
                };
            } catch (error) {
                console.error('Firebase initialization error:', error);
                setFirebaseError('Failed to initialize Firebase: ' + error.message);
                setLoading(false);
            }
        } else {
            setFirebaseError('Firebase configuration not found. Please configure Firebase in the plugin settings.');
            setLoading(false);
        }
    }, []);

    // Heartbeat mechanism to keep user status updated
    useEffect(() => {
        if (user && db) {
            //  console.log('Starting heartbeat for user:', user.email);
            
            // Update user status every 30 seconds to maintain online presence
            const heartbeatInterval = setInterval(() => {
                if (!document.hidden) {
                    updateUserStatus(user, true);
                    //  console.log('Heartbeat: Updated online status for', user.email);
                }
            }, 30000); // 30 seconds
            
            return () => {
                //  console.log('Stopping heartbeat for user:', user.email);
                clearInterval(heartbeatInterval);
            };
        }
    }, [user, db]);

    // Load support system settings and current user admin status
    useEffect(() => {
        if (user) {
            loadSupportSystemSettings();
            checkCurrentUserAdminStatus();
        }
    }, [user]);

    // Refresh support system settings periodically
    useEffect(() => {
        if (user) {
            const refreshInterval = setInterval(() => {
                loadSupportSystemSettings();
                checkCurrentUserAdminStatus();
            }, 30000); // Refresh every 30 seconds
            
            return () => clearInterval(refreshInterval);
        }
    }, [user]);

    // Periodic user list refresh to catch newly signed up users
    useEffect(() => {
        if (user && db) {
            //  console.log('Starting periodic user list refresh...');
            
            // Refresh user list every 60 seconds to catch new users
            const userListRefreshInterval = setInterval(() => {
                //  console.log('Refreshing user list to catch new users...');
                refreshUsersList();
            }, 60000); // 60 seconds
            
            return () => {
                //  console.log('Stopping periodic user list refresh');
                clearInterval(userListRefreshInterval);
            };
        }
    }, [user, db]);

    // Clean up any test users on component mount
    useEffect(() => {
        if (db) {
            cleanupTestUsers();
        }
    }, [db]);

    // Listen for users with real-time updates
    useEffect(() => {
        if (user && db) {
            //  console.log('Setting up users listener for admin...');
            
            const setupUsersListener = async () => {
                try {
                    // Listen to all users except current user with real-time updates
                    const usersQuery = query(
                        collection(db, 'users'),
                        where('uid', '!=', user.uid)
                    );
                    
                    const unsubscribe = onSnapshot(usersQuery, async (querySnapshot) => {
                        const userList = [];
                        querySnapshot.forEach((doc) => {
                            const userData = { id: doc.id, ...doc.data() };
                            // Filter out test users
                            if (!userData.uid.startsWith('test_user_')) {
                                // Check if user is truly online (last seen within 2 minutes)
                                const isRecentlyActive = userData.lastSeen && 
                                    userData.lastSeen.toDate && 
                                    (new Date() - userData.lastSeen.toDate()) < 120000; // 2 minutes
                                
                                userData.isOnline = userData.isOnline && isRecentlyActive;
                                userList.push(userData);
                            }
                        });
                        
                        // Fetch admin status for all users from WordPress database
                        if (userList.length > 0) {
                            try {
                                const usersWithAdminStatus = await fetchUsersWithAdminStatus(userList);
                                //  console.log('Users with admin status:', usersWithAdminStatus);
                                setUsers(usersWithAdminStatus);
                            } catch (error) {
                                console.error('Error fetching admin status:', error);
                                setUsers(userList); // Fallback to users without admin status
                            }
                        } else {
                            setUsers([]);
                        }
                        
                        setUsingLocalUsers(false);
                        //  console.log('Total users from Firestore:', userList.length);
                    }, (error) => {
                        console.error('Error listening for users in admin:', error);
                        setUsers([]);
                        setUsingLocalUsers(false);
                    });
                    
                    return () => unsubscribe();
                } catch (error) {
                    console.error('Error setting up users listener:', error);
                    setUsers([]);
                    setUsingLocalUsers(false);
                }
            };
            
            setupUsersListener();
        }
    }, [user, db]);

    // Listen for all messages to detect new users and unread messages
    useEffect(() => {
        if (user && db) {
            ////  console.log('Setting up global messages listener...');
            
            const setupGlobalMessagesListener = async () => {
                try {
                    // Listen to all chat messages
                    const messagesQuery = query(
                        collection(db, 'chats'),
                        orderBy('lastMessageTime', 'desc')
                    );
                    
                    const unsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
                        querySnapshot.forEach((chatDoc) => {
                            const chatId = chatDoc.id;
                            const chatData = chatDoc.data();
                            
                            // If this chat involves the current user
                            if (chatId.includes(user.uid)) {
                                const otherUserId = chatId.split('_').find(uid => uid !== user.uid);
                                
                                if (otherUserId && chatData.lastMessage) {
                                    // Update last message time
                                    setLastMessageTimes(prev => ({
                                        ...prev,
                                        [otherUserId]: chatData.lastMessage.timestamp
                                    }));
                                    
                                    // Check if message is unread (not from current user and not selected)
                                    if (chatData.lastMessage.uid !== user.uid && selectedUser?.uid !== otherUserId) {
                                        setUnreadMessages(prev => ({
                                            ...prev,
                                            [otherUserId]: (prev[otherUserId] || 0) + 1
                                        }));
                                    }
                                    
                                    // Auto-add user to sidebar if they're not already there
                                    if (chatData.lastMessage.uid !== user.uid) {
                                        const messageUser = chatData.lastMessage;
                                        setUsers(prevUsers => {
                                            const userExists = prevUsers.find(u => u.uid === messageUser.uid);
                                            if (!userExists) {
                                                // //  console.log('Auto-adding user to sidebar:', messageUser.displayName);
                                                return [...prevUsers, {
                                                    uid: messageUser.uid,
                                                    displayName: messageUser.displayName,
                                                    email: messageUser.email,
                                                    photoURL: messageUser.photoURL,
                                                    isOnline: false,
                                                    lastSeen: messageUser.timestamp
                                                }];
                                            }
                                            return prevUsers;
                                        });
                                    }
                                }
                            }
                        });
                    }, (error) => {
                        console.error('Error listening for global messages:', error);
                    });
                    
                    return () => unsubscribe();
                } catch (error) {
                    console.error('Error setting up global messages listener:', error);
                }
            };
            
            setupGlobalMessagesListener();
        }
    }, [user, db, selectedUser]);

    // Clean up test users
    const cleanupTestUsers = async () => {
        try {
            const usersQuery = query(collection(db, 'users'));
            const querySnapshot = await getDocs(usersQuery);
            
            const deletePromises = [];
            querySnapshot.forEach((doc) => {
                const userData = doc.data();
                if (userData.uid && userData.uid.startsWith('test_user_')) {
                    // //  console.log('Deleting test user:', userData.uid);
                    deletePromises.push(deleteDoc(doc.ref));
                }
            });
            
            if (deletePromises.length > 0) {
                await Promise.all(deletePromises);
                // //  console.log('Cleaned up test users');
            }
        } catch (error) {
            console.error('Error cleaning up test users:', error);
        }
    };

    // Refresh users list from Firestore
    const refreshUsersList = async () => {
        if (!db || !user) {
            // //  console.log('Cannot refresh users - no Firestore or user');
            return;
        }

        // //  console.log('Refreshing users list from Firestore...');
        
        try {
            const usersQuery = query(
                collection(db, 'users'),
                where('uid', '!=', user.uid)
            );
            
            const querySnapshot = await getDocs(usersQuery);
            const userList = [];
            
            querySnapshot.forEach((doc) => {
                const userData = { id: doc.id, ...doc.data() };
                // Filter out test users
                if (!userData.uid.startsWith('test_user_')) {
                    userList.push(userData);
                    // //  console.log('Found user in refresh:', userData.email);
                }
            });
            
            // //  console.log('Refreshed users list:', userList);
            setUsers(userList);
            setUsingLocalUsers(false);
            
        } catch (error) {
            console.error('Error refreshing users list:', error);
            setUsers([]);
            setUsingLocalUsers(false);
        }
    };

    // Listen for messages when a user is selected
    useEffect(() => {
        if (user && selectedUser && db) {
            const chatId = getChatId(user.uid, selectedUser.uid);
            const messagesQuery = query(
                collection(db, 'chats', chatId, 'messages'),
                orderBy('timestamp', 'asc')
            );
            
            const unsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
                const messageList = [];
                querySnapshot.forEach((doc) => {
                    messageList.push({ id: doc.id, ...doc.data() });
                });
                setMessages(messageList);
                
                // Clear unread messages for this user
                if (selectedUser.uid) {
                    setUnreadMessages(prev => {
                        const newUnread = { ...prev };
                        delete newUnread[selectedUser.uid];
                        return newUnread;
                    });
                }
            });
            
            return () => unsubscribe();
        }
    }, [user, selectedUser, db]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const updateUserStatus = async (user, isOnline) => {
        if (!db) {
            // //  console.log('No Firestore available for user status update');
            return;
        }
        
        try {
            // //  console.log('Saving user to Firestore:', user.email, 'UID:', user.uid);
            // //  console.log('User photoURL:', user.photoURL);
            
            // Save user to Firestore when they sign in
            await setDoc(doc(db, 'users', user.uid), {
                uid: user.uid,
                displayName: user.displayName || user.email,
                email: user.email,
                photoURL: user.photoURL || '',
                isOnline: isOnline,
                lastSeen: serverTimestamp(),
                createdAt: serverTimestamp()
            }, { merge: true });
            
            // //  console.log('User successfully saved to Firestore:', user.email);
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const getChatId = (uid1, uid2) => {
        return [uid1, uid2].sort().join('_');
    };

    const authenticateWithWordPress = async (firebaseUser) => {
        try {
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_firebase_auth',
                    nonce: getNonce(),
                    firebase_uid: firebaseUser.uid,
                    user_name: firebaseUser.displayName || firebaseUser.email,
                    user_email: firebaseUser.email,
                    user_avatar: firebaseUser.photoURL || '',
                    auth_provider: firebaseUser.providerData[0]?.providerId || 'email'
                })
            });
            
            const data = await response.json();
            if (!data.success) {
                console.error('WordPress authentication failed:', data.data);
            }
        } catch (error) {
            console.error('Error authenticating with WordPress:', error);
        }
    };

    const signInWithGoogle = async () => {
        if (!auth) return;
        
        const provider = new GoogleAuthProvider();
        // Add custom parameters to fix unauthorized domain issue
        provider.setCustomParameters({
            prompt: 'select_account'
        });
        
        try {
            const result = await signInWithPopup(auth, provider);
            // //  console.log('Google sign-in successful:', result.user.email);
        } catch (error) {
            console.error('Google sign-in error:', error);
            if (error.code === 'auth/unauthorized-domain') {
                setFirebaseError('Google sign-in failed: Unauthorized domain. Please add your domain to Firebase Authentication settings.');
            } else {
                setFirebaseError('Google sign-in failed: ' + error.message);
            }
        }
    };

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSignUpProfileImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setSignUpProfileImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadProfileImage = async (file) => {
        if (!storage || !file) return null;
        
        try {
            const storageRef = ref(storage, `profile-images/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading profile image:', error);
            return null;
        }
    };

    const signInWithEmail = async () => {
        if (!auth) return;
        
        try {
            if (isSignUp) {
                // Create user with email and password
                const userCredential = await createUserWithEmailAndPassword(auth, loginEmail, loginPassword);
                const newUser = userCredential.user;
                
                // Upload profile image if selected
                let photoURL = '';
                if (signUpProfileImage) {
                    photoURL = await uploadProfileImage(signUpProfileImage);
                }
                
                // Update profile with display name and photo
                await updateProfile(newUser, {
                    displayName: signUpDisplayName || loginEmail,
                    photoURL: photoURL
                });
                
                // //  console.log('User created successfully:', newUser.email);
            } else {
                await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
            }
            
            setShowLogin(false);
            setLoginEmail('');
            setLoginPassword('');
            setSignUpDisplayName('');
            setSignUpProfileImage(null);
            setSignUpProfileImagePreview('');
        } catch (error) {
            console.error('Email sign-in error:', error);
            setFirebaseError('Authentication failed: ' + error.message);
        }
    };

    const handleSignOut = async () => {
        if (!auth) return;
        
        try {
            // //  console.log('Signing out user...');
            
            // Update user status to offline before clearing state
            if (db && user) {
                await updateUserStatus(user, false);
            }
            
            // Clear all state first to prevent Firestore errors
            setUser(null);
            setSelectedUser(null);
            setMessages([]);
            setUsers([]);
            setSearchTerm('');
            setUsingLocalUsers(false);
            setFirebaseError(null);
            setLoading(false);
            setUnreadMessages({});
            setLastMessageTimes({});
            
            // Sign out from Firebase Auth
            await signOut(auth);
            
            // //  console.log('User signed out successfully');
        } catch (error) {
            console.error('Sign-out error:', error);
            // Even if there's an error, ensure state is cleared
            setUser(null);
            setSelectedUser(null);
            setMessages([]);
            setUsers([]);
            setSearchTerm('');
            setUsingLocalUsers(false);
            setFirebaseError(null);
            setLoading(false);
            setUnreadMessages({});
            setLastMessageTimes({});
        }
    };

    const sendMessage = async (text, type = 'text', fileUrl = null, fileName = null) => {
        if (!user || !selectedUser || !db || (!text.trim() && !fileUrl)) return;
        
        try {
            const chatId = getChatId(user.uid, selectedUser.uid);
            const messageData = {
                text: text,
                uid: user.uid,
                displayName: user.displayName || user.email,
                email: user.email,
                photoURL: user.photoURL || '',
                timestamp: serverTimestamp(),
                type: type
            };

            if (fileUrl) {
                messageData.fileUrl = fileUrl;
                messageData.fileName = fileName;
            }

            // Add message to chat
            await addDoc(collection(db, 'chats', chatId, 'messages'), messageData);
            
            // Update chat metadata with last message
            await setDoc(doc(db, 'chats', chatId), {
                lastMessage: messageData,
                lastMessageTime: serverTimestamp(),
                participants: [user.uid, selectedUser.uid]
            }, { merge: true });
            
            if (type === 'text') {
                setNewMessage('');
            }
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const handleFileUpload = async (file) => {
        if (!storage || !file) {
            console.error('No storage or file provided');
            return;
        }
        
        // Check file size (limit to 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            setFirebaseError('File too large. Maximum size is 10MB.');
            return;
        }
        
        setUploading(true);
        try {
            // //  console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);
            
            const storageRef = ref(storage, `chat-files/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            // //  console.log('File uploaded successfully:', snapshot);
            
            const downloadURL = await getDownloadURL(snapshot.ref);
            // //  console.log('Download URL:', downloadURL);
            
            const fileType = file.type.startsWith('image/') ? 'image' : 'file';
            await sendMessage('', fileType, downloadURL, file.name);
            
            // //  console.log('File message sent successfully');
        } catch (error) {
            console.error('Error uploading file:', error);
            setFirebaseError('File upload failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const addEmoji = (emoji) => {
        setNewMessage(prev => prev + emoji);
        setShowEmojiPicker(false);
    };

    const filteredUsers = users.filter(user => {
        // First apply support system filtering
        let shouldShow = true;
        
        
        if (supportSystemEnabled) {
            if (currentUserIsAdmin) {
                // Admin users can see everyone
                shouldShow = true;
                //  console.log('Current user is admin - showing all users');
            } else {
                // Regular users can only see admin users in support mode
                shouldShow = user.isAdmin === true;
            }
        } else {
            //  console.log('Support system disabled - showing all users');
        }
        // If support system is disabled, show all users (shouldShow remains true)
        
        if (!shouldShow) {
            //  console.log('User', user.email, 'filtered out by support system');
            return false;
        }
        
        // Then apply search filtering
        if (!searchTerm.trim()) return true;
        
        const searchLower = searchTerm.toLowerCase();
        const displayName = user.displayName?.toLowerCase() || '';
        const email = user.email?.toLowerCase() || '';
        const uid = user.uid?.toLowerCase() || '';
        
        const matches = displayName.includes(searchLower) || 
                       email.includes(searchLower) || 
                       uid.includes(searchLower);
        
        return matches;
    });

    // //  console.log('All users:', users);
    // //  console.log('Filtered users:', filteredUsers);
    // //  console.log('Search term:', searchTerm);
    // //  console.log('Search term length:', searchTerm.length);

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        // //  console.log('Search changed to:', value);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString();
        }
    };

    const getUnreadCount = (userId) => {
        return unreadMessages[userId] || 0;
    };

    const hasNewMessage = (userId) => {
        return getUnreadCount(userId) > 0;
    };

    // Fetch users with admin status from WordPress database
    const fetchUsersWithAdminStatus = async (firestoreUsers) => {
        try {
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_get_users',
                    nonce: getNonce(),
                    current_user_uid: user?.uid || ''
                })
            });
            
            const data = await response.json();
            if (data.success) {
                const wpUsers = data.data.users;
                
                // Merge Firestore users with WordPress admin status
                const mergedUsers = firestoreUsers.map(firestoreUser => {
                    const wpUser = wpUsers.find(wp => wp.firebase_uid === firestoreUser.uid);
                    const isAdmin = wpUser ? Boolean(wpUser.is_admin) : false;
                    
                    /* //  console.log('Admin Firebase - Merging user:', firestoreUser.email, {
                        wpUserFound: !!wpUser,
                        wpUserIsAdmin: wpUser ? wpUser.is_admin : 'N/A',
                        finalIsAdmin: isAdmin
                    }); */
                    
                    return {
                        ...firestoreUser,
                        isAdmin: isAdmin,
                        wpUserId: wpUser ? wpUser.id : null
                    };
                });
                
                //  console.log('Merged users with admin status:', mergedUsers);
                return mergedUsers;
            } else {
                console.error('Failed to fetch users from WordPress:', data.data);
                return firestoreUsers; // Return original users without admin status
            }
        } catch (error) {
            console.error('Error fetching users with admin status:', error);
            return firestoreUsers; // Return original users without admin status
        }
    };

    // Load support system settings
    const loadSupportSystemSettings = async () => {
        try {
            //  console.log('Loading support system settings...');
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_get_support_settings',
                    nonce: getNonce()
                })
            });
            
            const data = await response.json();
            //  console.log('Support settings response:', data);
            
            if (data.success) {
                setSupportSystemEnabled(data.data.support_system_enabled);
                //  console.log('Support system enabled set to:', data.data.support_system_enabled);
                //  console.log('Current supportSystemEnabled state:', supportSystemEnabled);
            } else {
                console.error('Failed to load support settings:', data.data);
            }
        } catch (error) {
            console.error('Error loading support settings:', error);
        }
    };

    // Check if current user is admin
    
    // Load support system settings
    

    // Check if current user is admin
    const checkCurrentUserAdminStatus = async () => {
        if (!user) return;
        
        try {
            const response = await fetch(adminAjax() || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'acb_check_user_admin_status',
                    nonce: getNonce(),
                    firebase_uid: user.uid
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setCurrentUserIsAdmin(data.data.is_admin);
                //  console.log('Current user is admin:', data.data.is_admin);
            } else {
                console.error('Failed to check admin status:', data.data);
                setCurrentUserIsAdmin(false);
            }
        } catch (error) {
            console.error('Error checking admin status:', error);
            setCurrentUserIsAdmin(false);
        }
    };


    if (loading) {
        return (
            <div className="acb-loading">
                <div className="acb-spinner"></div>
                <p>Loading chat...</p>
            </div>
        );
    }

    if (firebaseError) {
        return (
            <div className="acb-error">
                <h3>Configuration Error</h3>
                <p>{firebaseError}</p>
                <p>Please go to <strong>Admin Chat Box &gt; Settings</strong> to configure Firebase.</p>
                <button onClick={() => setFirebaseError(null)} className="acb-retry-btn">
                    Try Again
                </button>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="acb-auth-container">
                <div className="acb-auth-box">
                    <h2>Admin Chat Box</h2>
                    <p>Sign in to start chatting</p>
                    
                    <button 
                        className="acb-google-btn"
                        onClick={signInWithGoogle}
                    >
                        <i className="fab fa-google"></i>
                        Sign in with Google
                    </button>
                    
                    <div className="acb-divider">
                        <span>or</span>
                    </div>
                    
                    <button 
                        className="acb-email-btn"
                        onClick={() => setShowLogin(true)}
                    >
                        Sign in with Email
                    </button>
                    
                    {showLogin && (
                        <div className="acb-email-form">
                            {isSignUp && (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Display Name"
                                        value={signUpDisplayName}
                                        onChange={(e) => setSignUpDisplayName(e.target.value)}
                                    />
                                    <div className="acb-profile-image-section">
                                        <button 
                                            type="button"
                                            className="acb-upload-profile-btn"
                                            onClick={() => profileImageInputRef.current?.click()}
                                        >
                                            {signUpProfileImagePreview ? (
                                                <img 
                                                    src={signUpProfileImagePreview} 
                                                    alt="Profile preview" 
                                                    className="acb-profile-preview"
                                                />
                                            ) : (
                                                <i className="fas fa-camera"></i>
                                            )}
                                            {signUpProfileImagePreview ? 'Change Photo' : 'Upload Profile Photo'}
                                        </button>
                                        <input
                                            ref={profileImageInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleProfileImageChange}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                </>
                            )}
                            <input
                                type="email"
                                placeholder="Email"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                            />
                            <input
                                type="password"
                                placeholder="Password"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                            />
                            <div className="acb-form-actions">
                                <button onClick={signInWithEmail}>
                                    {isSignUp ? 'Sign Up' : 'Sign In'}
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setIsSignUp(!isSignUp);
                                        setSignUpDisplayName('');
                                        setSignUpProfileImage(null);
                                        setSignUpProfileImagePreview('');
                                    }}
                                >
                                    {isSignUp ? 'Already have account?' : 'Create account'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="acb-chat-container">
            {/* User List Sidebar */}
            <div className="acb-user-sidebar">
                <div className="acb-user-header">
                    <div className="acb-current-user">
                      
                         <SafeImage 
                            src={user.photoURL} 
                            alt={user.displayName || user.email}  
                            className="acb-user-avatar"
                            fallbackSrc="https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg"
                        />
                        
                        <div className="acb-user-info">
                            <h4>{user.displayName || user.email}</h4>
                            <span className="acb-online-status">Online</span>

                            <div style={{ 
                                fontSize: '12px', 
                                color: '#ffffff', 
                                marginTop: '8px',
                                padding: '2px 8px',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'baseline',
                                flexWrap: 'wrap',
                                flexDirection: 'row',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ opacity: '0.7' }}>Total:</span>
                                    <span style={{ fontWeight: '600' }}>{users.length}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ opacity: '0.7' }}>Filtered:</span>
                                    <span style={{ fontWeight: '600' }}>{filteredUsers.length}</span>
                                </div>
                                
                            </div>
                        </div>
                    </div>
                    <button onClick={handleSignOut} className="acb-signout-btn">
                        <i className="fas fa-sign-out-alt"></i>
                    </button>
                </div>

                <div className="acb-search-container">
                    <input
                        type="text"
                        placeholder="Search users by name, email, or ID..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="acb-search-input"
                        style={{ flex: 1, marginRight: '8px' }}
                    />
                    <button 
                        onClick={refreshUsersList} 
                        className="acb-refresh-users-btn"
                        style={{ marginLeft: '0px', padding: '5px', background: '#94a3b8', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                        title="Refresh Users List"
                    >
                        <i className="fas fa-sync-alt"></i>
                    </button>

                </div>

                <div className="acb-users-list">
                    {filteredUsers.length === 0 ? (
                        <div className="acb-no-users">
                            <p>No users found</p>
                            {searchTerm && <p>Try a different search term</p>}
                            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                                Only users who have signed in will appear here
                            </p>
                        </div>
                    ) : (
                        filteredUsers.map((userItem) => (
                            <div
                                key={userItem.uid}
                                className={`acb-user-item ${selectedUser?.uid === userItem.uid ? 'active' : ''} ${hasNewMessage(userItem.uid) ? 'has-new-message' : ''}`}
                                onClick={() => setSelectedUser(userItem)}
                            >
                                <div className="acb-user-avatar-container">
                                    <SafeImage 
                                        src={userItem.photoURL}
                                        alt={userItem.displayName || userItem.email}
                                        className="acb-user-avatar"
                                        fallbackSrc="https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg"
                                    />
                                    <span className={`acb-online-indicator ${userItem.isOnline ? 'online' : 'offline'}`}></span>
                                    {hasNewMessage(userItem.uid) && (
                                        <span className="acb-new-message-indicator">
                                            {getUnreadCount(userItem.uid)}
                                        </span>
                                    )}
                                </div>
                                <div className="acb-user-details">
                                    <h5>{userItem.displayName || userItem.email}</h5>
                                    <span className="acb-user-status">
                                        {userItem.isOnline ? 'Online' : 'Offline'}{` `}
                                    </span>
                                    {lastMessageTimes[userItem.uid] && (
                                        <span className="acb-last-message-time">
                                            {formatTime(lastMessageTimes[userItem.uid])}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Chat Area */}
            <div className="acb-chat-area">
                {selectedUser ? (
                    <>
                        {/* Chat Header */}
                        <div className="acb-chat-header">
                            <div className="acb-chat-user-info">
                                {/* <img 
                                    src={selectedUser.photoURL || 'https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg'} 
                                    alt={selectedUser.displayName || selectedUser.email}
                                    className="acb-user-avatar"
                                /> */}
                                <SafeImage 
                                    src={selectedUser.photoURL} 
                                    alt={selectedUser.displayName || selectedUser.email}  
                                    className="acb-user-avatar"
                                    fallbackSrc="https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg"
                                />
                                <div>
                                    <h4>{selectedUser.displayName || selectedUser.email}</h4>
                                    <span className={`acb-online-status ${selectedUser.isOnline ? 'online' : 'offline'}`}>
                                        {selectedUser.isOnline ? 'Online' : 'Offline'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="acb-messages-container">
                            {messages.map((message, index) => {
                                const isOwn = message.uid === user.uid;
                                const showDate = index === 0 || 
                                    formatDate(message.timestamp) !== formatDate(messages[index - 1]?.timestamp);
                                
                                return (
                                    <div key={message.id}>
                                        {showDate && (
                                            <div className="acb-date-divider">
                                                {formatDate(message.timestamp)}
                                            </div>
                                        )}
                                        <div className={`acb-message ${isOwn ? 'own' : 'other'}`}>
                                            {/* <img 
                                                src={message.photoURL || 'https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg'} 
                                                alt={message.displayName}
                                                className="acb-message-avatar"
                                            /> */}
                                            <SafeImage 
                                                src={message.photoURL} 
                                                alt={message.displayName || message.email}  
                                                className="acb-message-avatar"
                                                fallbackSrc="https://static.vecteezy.com/system/resources/previews/022/059/000/non_2x/no-image-available-icon-vector.jpg"
                                            />
                                            <div className="acb-message-content">
                                                
                                                <div className="acb-message-body">
                                                    {message.type === 'image' ? (
                                                        <img 
                                                            src={message.fileUrl} 
                                                            alt="Shared image" 
                                                            className="acb-message-image"
                                                            onClick={() => window.open(message.fileUrl, '_blank')}
                                                        />
                                                    ) : message.type === 'file' ? (
                                                        <div className="acb-message-file">
                                                            <i className="fas fa-file"></i>
                                                            <a href={message.fileUrl} target="_blank" rel="noopener noreferrer">
                                                                {message.fileName}
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <p>{message.text}</p>
                                                    )}
                                                </div>
                                                <div className="acb-message-header">
                                                    <span className="acb-message-sender">{message.displayName}</span>
                                                    <span className="acb-message-time">{formatTime(message.timestamp)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Message Input */}
                        <div className="acb-message-input">
                            <div className="acb-input-actions">
                                <button 
                                    className="acb-emoji-btn"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                >
                                    😊
                                </button>
                                <button 
                                    className="acb-attach-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                >
                                    <i className="fas fa-paperclip"></i>
                                </button>
                            </div>
                            
                            {showEmojiPicker && (
                                <div className="acb-emoji-picker">
                                    {['😊', '😂', '❤️', '👍', '👎', '🎉', '🔥', '💯', '😎', '🤔', '😢', '😡'].map(emoji => (
                                        <button 
                                            key={emoji} 
                                            onClick={() => addEmoji(emoji)}
                                            className="acb-emoji-btn"
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            )}
                            
                            <input
                                type="text"
                                placeholder="Type a message..."
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && sendMessage(newMessage)}
                                className="acb-message-text"
                                disabled={uploading}
                            />
                            
                            <button 
                                onClick={() => sendMessage(newMessage)}
                                disabled={!newMessage.trim() && !uploading}
                                className="acb-send-btn"
                            >
                                {uploading ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                    <i className="fas fa-paper-plane"></i>
                                )}
                            </button>
                            
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        handleFileUpload(file);
                                    }
                                    e.target.value = '';
                                }}
                                style={{ display: 'none' }}
                                accept="image/*,.pdf,.doc,.docx,.txt"
                            />
                        </div>
                    </>
                ) : (
                    <div className="acb-no-chat-selected">
                        <div className="acb-welcome-message">
                            <i className="fas fa-comments"></i>
                            <h3>Welcome to Admin Chat Box</h3>
                            <p>Select a user from the sidebar to start chatting</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Firebase; 